import React from "react";
import { Link } from "react-router-dom";
import "../../Styles/ann-tokens.css";
import "../../Styles/marketing-base.css";
import "../../Styles/pages/staffing-firms-b.css";
import useReveal from "./useReveal";

/* ============================================================
   Ann for Recruitment Firms — "Ann for Recruitment Firms
   (standalone)" (Claude Design import)
   ============================================================ */

const UNIVERSE = [
  { label: "Competitors", top: "7%", left: "50%" },
  { label: "Peers", top: "23%", left: "81%" },
  { label: "Adjacent cos", top: "53%", left: "86%" },
  { label: "Culture", top: "87%", left: "68%" },
  { label: "Scale", top: "87%", left: "32%" },
  { label: "Stage", top: "53%", left: "14%" },
  { label: "Hidden talent", top: "23%", left: "19%" },
];

const CYCLE = [
  { label: "Assignment" },
  { label: "Define Success" },
  { label: "Search" },
  { label: "Engage" },
  { label: "Evaluate" },
  { label: "Placement", hot: true },
];

const LADDER = [
  { n: "01", label: "Understanding", h: 46 },
  { n: "02", label: "Better search", h: 82 },
  { n: "03", label: "Better candidates", h: 120 },
  { n: "04", label: "Greater trust", h: 162 },
  { n: "05", label: "Repeat business", h: 206 },
  { n: "06", label: "Revenue", h: 252 },
];

function Kick({ n, label, dark }) {
  return (
    <div className={`kick${dark ? " on-dark" : ""}`}>
      <span className="n">{n}</span>
      <span className="r" />
      <span className="l">{label}</span>
    </div>
  );
}

export default function StaffingFirmsB() {
  const ref = useReveal();

  return (
    <div className="sfb" ref={ref}>
      {/* HERO */}
      <section className="sfb-hero">
        <div className="wrap">
          <p className="tagline">
            <span className="rule" />
            For Recruitment &amp; Executive Search Firms
          </p>
          <h1>
            <span className="dim">Clients don&rsquo;t pay you to search more.</span>
            <span className="bright">
              They pay you to <em>get it right.</em>
            </span>
          </h1>
          <div className="lines">
            <p className="l1">Not more searches &mdash; better ones.</p>
            <p className="l2">Deeper understanding. Smarter discovery. Stronger evidence.</p>
            <p className="l3">That&rsquo;s how great firms get it right.</p>
          </div>
          <Link to="/send-a-role" className="btn">
            Send Ann a role <span className="rarr">&rarr;</span>
          </Link>
          <p className="fine">Receive a free live project within 48 hours.</p>
        </div>
      </section>

      {/* 01 · RoleDNA */}
      <section className="sfb-role">
        <div className="wrap">
          <Kick n="01" label="Great searches begin before search begins" />
          <div className="grid">
            <div>
              <p className="tag">Success, defined first</p>
              <h2>Clients see their own thinking reflected back.</h2>
              <p className="body">
                Ann writes the success definition down before search begins &mdash; a
                document sharp enough to put in front of your client. Alignment
                happens on page one, in your normal kickoff. And once the
                definition is agreed, scope creep becomes a conversation, not a
                rework.
              </p>
            </div>
            <div>
              <div className="sfb-flow reveal">
                <div className="line"><i /></div>
                <div className="nodes">
                  <div>
                    <span className="node">1</span>
                    <div className="nlabel">Stakeholders</div>
                  </div>
                  <div>
                    <span className="node hot">2</span>
                    <div className="nlabel hot">Success Definition</div>
                  </div>
                  <div>
                    <span className="node">3</span>
                    <div className="nlabel">Recruiter</div>
                  </div>
                  <div>
                    <span className="node">4</span>
                    <div className="nlabel">Search</div>
                  </div>
                </div>
              </div>
              <div className="pills">
                <span className="pill">Shared understanding</span>
                <span className="pill">Better alignment</span>
                <span className="pill">Less rework</span>
                <span className="pill">Fewer mid-search changes</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 02 · Search Universe */}
      <section className="sfb-universe">
        <div className="wrap sec-inner">
          <Kick n="02" label="Search changes when understanding changes" dark />
          <div className="flex">
            <div className="col-text">
              <h2>
                Ann searches within your client&rsquo;s world &mdash; and keeps
                expanding until the best fit is found.
              </h2>
              <div className="modes">
                <div className="smode">
                  <span className="dot" />
                  <span className="nm">QuickSearch</span>
                  <span className="ds">Everyday hiring, accelerated.</span>
                </div>
                <div className="smode">
                  <span className="dot ring" />
                  <span className="nm">TalentGPS</span>
                  <span className="ds">Leadership and niche talent, uncovered worldwide.</span>
                </div>
              </div>
              <div className="stuck">
                <p>
                  Search getting stuck?
                  <br />
                  Ann proposes new search directions.
                </p>
              </div>
            </div>
            <div className="sfb-orbit-wrap">
              <div className="sfb-orbit reveal">
                <div className="ring r1" />
                <div className="ring r2" />
                <div className="core">
                  <span className="lbl">CLIENT&rsquo;S WORLD</span>
                  <span className="txt">
                    Success
                    <br />
                    Definition
                  </span>
                </div>
                {UNIVERSE.map((u) => (
                  <span
                    key={u.label}
                    className="chip"
                    style={{ top: u.top, left: u.left }}
                  >
                    {u.label}
                  </span>
                ))}
              </div>
              <p className="caption">
                Search expands across every dimension until the best fit is found
              </p>
            </div>
          </div>
          <div className="sfb-take">
            <span className="lbl">Takeaway</span>
            <p>
              A Company DNA per client &mdash; every search tuned to that client&rsquo;s
              world, not a generic template.
            </p>
          </div>
        </div>
      </section>

      {/* 03 · Stop searching your own search */}
      <section className="sfb-compare">
        <div className="wrap">
          <Kick n="03" label="Stop searching your own search" />
          <h2>Stop searching within your search results.</h2>
          <p className="body">
            The database was never the problem. What happens after it answers is.
          </p>
          <div className="trows">
            <div className="trow trad">
              <p className="lbl">Traditional</p>
              <div className="chain">
                <span>Hundreds of profiles</span>
                <span className="rarr">&rarr;</span>
                <span>as many reviewed as time allows</span>
                <span className="rarr">&rarr;</span>
                <span>a shortlist of those</span>
              </div>
            </div>
            <div className="trow ann">
              <p className="lbl">Ann</p>
              <div className="chain">
                <span>Hundreds of profiles</span>
                <span className="rarr">&rarr;</span>
                <span>every one evaluated against the definition</span>
                <span className="rarr">&rarr;</span>
                <span className="final">a shortlist, ranked, with reasons</span>
              </div>
            </div>
          </div>
          <p className="pullquote">
            Not a longlist. A shortlist of people most likely to succeed &mdash; and
            the reasoning to stand behind each one.
          </p>
        </div>
      </section>

      {/* 04 · Client Conversations */}
      <section className="sfb-conv">
        <div className="wrap sec-inner">
          <Kick n="04" label="Every assignment becomes a client conversation" dark />
          <div className="grid">
            <h2>
              Every assignment delivers more than candidates.
              <br />
              <span className="bright">It delivers confidence.</span>
            </h2>
            <div className="diagram-wrap">
              <div className="sfb-cycle">
                <div className="rail" aria-hidden="true">
                  <div className="comet">
                    <span className="comet-fill" />
                  </div>
                  {CYCLE.map((c, i) => (
                    <div
                      key={c.label}
                      className={`step st-${i + 1}${c.hot ? " hot" : ""}`}
                    >
                      <span className="dot" />
                      <span className="n">{String(i + 1).padStart(2, "0")}</span>
                      <span className="lbl">{c.label}</span>
                    </div>
                  ))}
                </div>
                <div className="loop">
                  <span className="arr">&#8635;</span>
                  <p>
                    The client comes back &mdash; and it&rsquo;s the next
                    Assignment.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="sfb-take">
            <span className="lbl">Takeaway</span>
            <p>Every assignment becomes a demonstration of your capability.</p>
          </div>
        </div>
      </section>

      {/* 05 · Recruiter leverage */}
      <section className="sfb-leverage">
        <div className="wrap">
          <Kick n="05" label="Recruiter leverage" />
          <h2>Same team. More assignments. Better delivery.</h2>
          <p className="lede">Ann runs the grind. Your recruiters run the relationships.</p>
          <p className="body">
            Ann carries the defining, searching, screening, scheduling and
            documenting. Your recruiters carry what wins firms their
            reputations &mdash; client trust, candidate judgement, closing.
          </p>
        </div>
      </section>

      {/* 06 · Better decisions compound */}
      <section className="sfb-ladder">
        <div className="wrap sec-inner">
          <Kick n="06" label="Business outcomes" dark />
          <div className="head-grid">
            <h2>Better decisions compound.</h2>
            <p className="body">
              Clarity upstream creates speed and accuracy downstream &mdash; and
              better work compounds into repeat business. Most firms never get
              to run this loop; delivery eats the week. Ann gives the week
              back.
            </p>
          </div>
          <div className="bars">
            {LADDER.map((l, i) => {
              const last = i === LADDER.length - 1;
              const tone = last ? "#0f9aab" : `rgba(63,208,223,${0.22 + i * 0.13})`;
              return (
                <div key={l.n} className="step reveal">
                  <span className="n" style={{ color: last ? "#3fd0df" : "#8a9198" }}>
                    {l.n}
                  </span>
                  <div className="bar" style={{ height: l.h, background: tone }} />
                  <span className="lbl" style={{ color: last ? "#3fd0df" : "#e8e6e0" }}>
                    {l.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CLOSE */}
      <section className="sfb-close">
        <div className="wrap sec-inner">
          <p className="quote">Clients don&rsquo;t pay you to search. They pay you to get it right.</p>
          <h2>Now you can show them you did &mdash; every time, with evidence.</h2>
          <p className="lede">
            Send Ann a role. Get understanding, evidence and candidates worth
            standing behind &mdash; and the client conversation that wins the next
            assignment.
          </p>
          <Link to="/send-a-role" className="btn">
            Send Ann a role <span className="rarr">&rarr;</span>
          </Link>
          <p className="fine">Receive a free live project within 48 hours.</p>
        </div>
      </section>
    </div>
  );
}
